import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Chip, InputLabel, TextField } from "@mui/material";
import { palette } from "../../../styles/theme";
import { uniqueStrings } from "../specsEditorUtils";
import { getDebugLabelProps } from "./shared";
import { specsEditorFieldStyles } from "./styles";

export function SpecsMultiAutocompleteField({
  label,
  value,
  options,
  onChange,
  placeholder,
  debugPath,
  clickToEdit,
  error,
  errorWhenEmpty,
  helperText,
}: {
  label: React.ReactNode;
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  debugPath?: string;
  clickToEdit?: boolean;
  error?: boolean;
  errorWhenEmpty?: boolean;
  helperText?: React.ReactNode;
}) {
  const debugLabelProps = getDebugLabelProps(debugPath);
  const [editing, setEditing] = useState(!clickToEdit);
  const hasError = Boolean(error || (errorWhenEmpty && value.length === 0));

  useEffect(() => {
    if (!clickToEdit) {
      setEditing(true);
    }
  }, [clickToEdit]);

  return (
    <Box>
      <InputLabel
        onClick={debugLabelProps.onClick}
        sx={{
          mb: 0.5,
          fontSize: 13,
          color: hasError ? palette.red.error : undefined,
          ...debugLabelProps.sx,
        }}
      >
        {label}
      </InputLabel>
      {editing ? (
        <Autocomplete
          multiple
          freeSolo
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              alignItems: "flex-start",
              gap: 0.5,
              ...(hasError
                ? specsEditorFieldStyles.specsMultiAutocompleteError
                : {}),
            },
            "& .MuiAutocomplete-input": {
              flexBasis: "100% !important",
              minWidth: "100% !important",
              marginTop: "4px",
            },
          }}
          options={options}
          value={value}
          onBlur={() => {
            if (clickToEdit) {
              setEditing(false);
            }
          }}
          onChange={(_, nextValue) => onChange(uniqueStrings(nextValue))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={clickToEdit}
              error={hasError}
              helperText={helperText}
              placeholder={placeholder}
              size="small"
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
            />
          )}
        />
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setEditing(true);
            }
          }}
          sx={{
            ...specsEditorFieldStyles.specsMultiAutocompletePreview,
            color: value.length ? "text.primary" : "text.secondary",
            ...(hasError
              ? specsEditorFieldStyles.specsMultiAutocompleteError
              : {}),
          }}
        >
          {value.length ? value.join(", ") : "Click to edit"}
        </Box>
      )}
    </Box>
  );
}
